'use client';

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatVolume } from '@/lib/format';
import CoinLogo from './CoinLogo';

interface LiqRecent {
  id: number;
  exchange: string;
  symbol: string;
  side: string;
  price: number;
  amount: number;
  value: number;
  time: string;
}

export default function LiqTicker({ language }: { language: string }) {
  const { data } = useQuery({
    queryKey: ['liq-ticker'],
    queryFn: () => api.get<LiqRecent[]>('/liquidations?limit=30'),
    refetchInterval: 5_000,
  });

  const items = data ?? [];
  if (items.length === 0) return null;

  // Duplicate for seamless scroll
  const loop = [...items, ...items];
  // Mobile: show the latest 2 statically for a cleaner view
  const mobile = items.slice(0, 2);

  const TickerItem = ({ liq, dupKey }: { liq: LiqRecent; dupKey: number }) => {
    const base = liq.symbol.replace('USDT', '');
    const isBig = liq.value >= 100_000;
    return (
      <a
        key={`${liq.id}-${dupKey}`}
        href={`/coin/${liq.symbol}`}
        className={`flex shrink-0 items-center gap-1.5 rounded-md px-1.5 py-0.5 sm:gap-2 sm:px-2 sm:py-1 ${
          isBig ? 'bg-gf-negative/10 ring-1 ring-gf-negative/30' : ''
        }`}
      >
        <CoinLogo symbol={liq.symbol} size={16} />
        <span className="text-[11px] font-medium sm:text-xs">{base}</span>
        <span className={`rounded px-1 py-0.5 text-[9px] font-medium sm:px-1.5 sm:text-[10px] ${
          liq.side === 'long'
            ? 'bg-gf-positive/20 text-gf-positive'
            : 'bg-gf-negative/20 text-gf-negative'
        }`}>
          {liq.side === 'long'
            ? (language !== 'en' ? '多' : 'L')
            : (language !== 'en' ? '空' : 'S')}
        </span>
        <span className={`font-mono text-[11px] font-bold sm:text-xs ${
          isBig ? 'text-gf-negative' : 'text-gf-text'
        }`}>
          {formatVolume(liq.value)}
        </span>
        <span className="hidden text-[10px] text-gf-muted sm:inline">
          {new Date(parseInt(liq.time)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </span>
        <span className="hidden text-[10px] text-gf-muted sm:inline">{liq.exchange}</span>
      </a>
    );
  };

  return (
    <div className="relative mb-3 overflow-hidden rounded-lg border border-gf-border bg-gf-surface2/50 py-1.5 sm:py-2">
      <div className="absolute left-0 top-0 z-10 flex h-full items-center bg-gf-accent px-2 text-[10px] font-bold text-white sm:px-3 sm:text-xs">
        {language !== 'en' ? '实时' : 'LIVE'}
      </div>
      {/* Mobile: static latest 2 */}
      <div className="flex items-center gap-4 overflow-x-auto pl-14 pr-4 sm:hidden">
        {mobile.map((liq, i) => <TickerItem key={`m${liq.id}-${i}`} liq={liq} dupKey={i} />)}
      </div>
      {/* Desktop: seamless scroll */}
      <div className="liq-ticker-track hidden items-center gap-4 pl-14 pr-4 sm:flex sm:gap-6 sm:pl-24">
        {loop.map((liq, i) => <TickerItem key={`d${liq.id}-${i}`} liq={liq} dupKey={i} />)}
      </div>
      <style jsx>{`
        .liq-ticker-track {
          animation: liq-scroll 60s linear infinite;
        }
        .liq-ticker-track:hover {
          animation-play-state: paused;
        }
        @keyframes liq-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
